<template>
  <div
    v-if="!loading"
    class="tree-navigation"
  >
    <div class="tree-header d-flex justify-content-between py-2 px-3">
      <div>
        <span class="fal fa-chart-network mr-2" />
        <span>{{ caseNumber }}</span>
      </div>
      <div
        class="times-button"
        @click="$emit('closeTree')"
      >
        <span class="far fa-times" />
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="!isEmpty(items)"
        class="tree-scroll"
      >
        <liquor-tree
          ref="tree"
          :data="items"
          :options="treeOptions"
          @tree:mounted="onTreeMounted"
          @node:expanded="onNodeExpanded"
          @node:collapsed="onNodeCollapsed"
          @node:selected="onNodeSelected"
          @node:clicked="onNodeClicked"
        >
          <div
            slot-scope="{ node }"
            :class="{'check-selected':node.states.checked}"
          >
            <div v-if="node.data.entityType === ATTACHMENT_ENTITY_TYPE.SAMPLE">
              <span class="fal fa-prescription-bottle mr-2" />
              <span>{{ node.text }}</span>
            </div>
            <div
              v-if="node.data.entityType === ATTACHMENT_ENTITY_TYPE.PORTION"
              class="tree-row"
            >
              <span class="icon-block mr-2" />
              <span>{{ node.text }}</span>
            </div>
            <div
              v-if="node.data.entityType === ATTACHMENT_ENTITY_TYPE.SUBPORTION"
              class="tree-row"
            >
              <span class="icon-porta mr-2" />
              <span>{{ node.text }}</span>
            </div>

            <div
              v-if="node.data.entityType === ATTACHMENT_FAKE"
            />

            <div
              v-if="node.data.entityType === ATTACHMENT_SAMPLE_ENTITY || node.data.entityType === ATTACHMENT_SUBPORTION_ENTITY"
              class="attachment"
            >
              <div>
                <div class="img-zone d-flex justify-content-center align-items-center">
                  <img
                    class="img-fluid treenode-attachment"
                    :src="getThumbnailImage(node.data)"
                    alt="Thumbnail Image"
                    aria-hidden="true"
                  >
                </div>
                <div>
                  <span
                    v-if="node.parent.data.entityType === ATTACHMENT_ENTITY_TYPE.PORTION"
                    class="icon-porta mr-2"
                  />
                  <p>{{ node.text }}</p>
                </div>
              </div>
            </div>
          </div>
        </liquor-tree>
      </div>
    </transition>
  </div>
  <div
    v-else
    class="spinner-wrapper"
  >
    <vt-loading />
  </div>
</template>

<script>
import LiquorTree from 'liquor-tree'
import sampleAttachmentService from '@/api/resources/attachment/sampleAttachmentService'
import subportionAttachmentService from '@/api/resources/attachment/subportionAttachmentService'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import { isEmpty } from 'lodash'
import caseService from '@/api/resources/common/commonCaseServices'

export default {
    name: 'AttachmentsTree',
    components: {
        LiquorTree
    },
    props: {
        laboratoryId: Number,
        caseId: Number,
        selectedEntity: { type: Object, default: () => { return {} } },
        query: { type: Object }
    },
    data() {
        return {
            treeOptions: {
                checkbox: false
            },
            caseNumber: null,
            loading: false,
            currentAttachmentSelectedId: null,
            items: [],
            lastSelectedNode: null,
            selectByFileName: null
        }
    },
    computed: {
        CURRENT_QUERY_ATTACHMENT_ID() {
            return this.query && Number(this.query.attachmentId)
        },
        ATTACHMENT_ENTITY_TYPE() {
            return ATTACHMENT_ENTITY_TYPE
        },
        ATTACHMENT_SAMPLE_ENTITY() {
            return 'ATTACHMENT_SAMPLE'
        },
        ATTACHMENT_SUBPORTION_ENTITY() {
            return 'ATTACHMENT_SUBPORTION'
        },
        ATTACHMENT_FAKE() {
            return 'ATTACHMENT_FAKE'
        }
    },
    async mounted() {
        await this.getHierarchicalCaseIndexTree()
    },
    methods: {
        async onNodeExpanded(node) {
            const nodeElement = this.findNodeByEntityIdAndType(node.data.id, node.data.entityType)
            if (node.data.entityType === ATTACHMENT_ENTITY_TYPE.SAMPLE) {
                const [error, response] = await this.toCall(this.getSampleMacroImageAttachments(node.data.id))
                if (error) {
                    this.showErrorModalGlobalFromErrorResponse(error)
                    return
                }
                const attachments = response.filter(attachment => {
                    const found = this.$refs.tree.find({ data: { entityType: this.ATTACHMENT_SAMPLE_ENTITY, attachmentId: attachment.attachmentId } })
                    return !found[0]
                })
                attachments.forEach(attachment => {
                    let isCurrentAttachmentShown = false
                    if (this.selectByFileName) {
                        isCurrentAttachmentShown = attachment.filename === this.selectByFileName
                        if (isCurrentAttachmentShown) {
                            this.query = {
                                laboratoryId: this.laboratoryId,
                                caseId: this.caseId,
                                sampleId: node.data.id,
                                label: this.query.label,
                                attachmentId: attachment.attachmentId
                            }
                            this.selectByFileName = null
                        }
                    } else {
                        isCurrentAttachmentShown = attachment.attachmentId === this.CURRENT_QUERY_ATTACHMENT_ID && !this.query.subportionId
                    }

                    const attachmentNodeElement = {
                        text: attachment.filename,
                        state: {
                            selected: isCurrentAttachmentShown
                        },
                        data: {
                            ...attachment,
                            entityType: this.ATTACHMENT_SAMPLE_ENTITY
                        }
                    }
                    nodeElement.prepend(attachmentNodeElement)
                })
            } else if (node.data.entityType === ATTACHMENT_ENTITY_TYPE.SUBPORTION) {
                const [error, response] = await this.toCall(this.getSubportionMacroImageAttachments(node.parent.parent.data.id, node.parent.data.id, node.data.id))
                if (error) {
                    this.showErrorModalGlobalFromErrorResponse(error)
                    return
                }
                const attachments = response.filter(attachment => {
                    const found = this.$refs.tree.find({ data: { entityType: this.ATTACHMENT_SUBPORTION_ENTITY, attachmentId: attachment.attachmentId } })
                    return !found[0]
                })
                attachments.forEach(attachment => {
                    let isCurrentAttachmentShown = false
                    if (this.selectByFileName) {
                        isCurrentAttachmentShown = attachment.filename === this.selectByFileName
                        if (isCurrentAttachmentShown) {
                            this.query = {
                                laboratoryId: this.laboratoryId,
                                caseId: this.caseId,
                                sampleId: node.parent.parent.data.id,
                                portionId: node.parent.data.id,
                                subportionId: node.data.id,
                                label: this.query.label,
                                attachmentId: attachment.attachmentId
                            }
                            this.selectByFileName = null
                        }
                    } else {
                        isCurrentAttachmentShown = attachment.attachmentId === this.CURRENT_QUERY_ATTACHMENT_ID && !!this.query.subportionId
                    }
                    const attachmentNodeElement = {
                        text: attachment.filename,
                        state: {
                            selected: isCurrentAttachmentShown
                        },
                        data: {
                            ...attachment,
                            entityType: this.ATTACHMENT_SUBPORTION_ENTITY
                        }
                    }
                    nodeElement.prepend(attachmentNodeElement)
                })
            }
        },
        findNodeByEntityIdAndType(id, entityType) {
            return this.$refs.tree.find({ data: { id: id, entityType: entityType } })[0]
        },
        getUrlSampleFile(sampleId, attachmentId) {
            return sampleAttachmentService.getUrlFile(this.laboratoryId, this.caseId, sampleId, attachmentId)
        },
        onNodeClicked(node) {
            if (!this.lastSelectedNode) {
                this.lastSelectedNode = node?.tree?.selectedNodes[0]
            }
        },
        onNodeSelected(node) {
            let query = null
            const attachmentId = node.data.attachmentId
            if (node.data.entityType === this.ATTACHMENT_SAMPLE_ENTITY &&
                    node.parent.data.entityType === ATTACHMENT_ENTITY_TYPE.SAMPLE) {
                if ((this.query.subportionId) ||
                (!this.query.subportionId && attachmentId !== this.CURRENT_QUERY_ATTACHMENT_ID)) {
                    const sampleId = node.parent.data.id
                    query = {
                        laboratoryId: this.laboratoryId,
                        caseId: this.caseId,
                        sampleId: sampleId,
                        label: node.parent.data.label,
                        attachmentId: attachmentId
                    }
                }
            } else if (node.data.entityType === this.ATTACHMENT_SUBPORTION_ENTITY &&
                    node.parent.data.entityType === ATTACHMENT_ENTITY_TYPE.SUBPORTION) {
                if (!this.query.subportionId ||
                (this.query.subportionId && attachmentId !== this.CURRENT_QUERY_ATTACHMENT_ID)) {
                    const subportionId = node.parent.data.id
                    const portionId = node.parent.parent.data.id
                    const sampleId = node.parent.parent.parent.data.id
                    query = {
                        laboratoryId: this.laboratoryId,
                        caseId: this.caseId,
                        sampleId: sampleId,
                        portionId: portionId,
                        subportionId: subportionId,
                        label: node.parent.data.label,
                        attachmentId: attachmentId
                    }
                }
            }

            if (query) {
                this.$emit('attachmentSelected', query, this.lastSelectedNode)
            }

            this.lastSelectedNode = node
        },
        onNodeCollapsed(node) {
            this.$nextTick(() => {
                node.data.attachments = []
            })
        },
        getThumbnailImage(attachment) {
            if (!attachment.thumbnailBytes) {
                return null
            } else if (attachment.thumbnailBytes.startsWith('data:')) {
                return attachment.thumbnailBytes
            } else {
                return `data:${attachment.mimetype};base64, ${attachment.thumbnailBytes}`
            }
        },
        onTreeMounted() {
            const selectedEntity = this.$refs.tree.find({
                data: {
                    entityType: this.selectedEntity.entityType,
                    id: this.selectedEntity.id
                }
            })
            if (selectedEntity) {
                selectedEntity.expand()
                let entity = selectedEntity[0]
                while (entity.parent) {
                    entity = entity.parent
                    entity.expand()
                }
            }
        },
        isEmpty(value) {
            return isEmpty(value)
        },
        mapNodes(node, entityType) {
            let parsedNode = {
                text: node.supSubportionnumber || node.porPortionnumber || node.samSampleNumber,
                state: {},
                data: {
                    id: node.supId || node.porId || node.samId,
                    entityType: entityType,
                    label: node.samLabel || node.porLabel || node.supLabel
                },
                children: []
            }
            if (entityType === ATTACHMENT_ENTITY_TYPE.SAMPLE) {
                parsedNode.state.selectable = false
                node.portions.forEach(portion => {
                    parsedNode.children.push(this.mapNodes(portion, ATTACHMENT_ENTITY_TYPE.PORTION))
                })
                if (node.attachmentsCount > 0) {
                    parsedNode.children.push(this.mapNodes({}, this.ATTACHMENT_FAKE))
                }
            } else if (entityType === ATTACHMENT_ENTITY_TYPE.PORTION) {
                parsedNode.state.selectable = false
                node.subportions.forEach(subportion => {
                    parsedNode.children.push(this.mapNodes(subportion, ATTACHMENT_ENTITY_TYPE.SUBPORTION))
                })
            } else if (entityType === ATTACHMENT_ENTITY_TYPE.SUBPORTION) {
                parsedNode.state.selectable = false
                if (node.attachmentsCount > 0) {
                    parsedNode.children.push(this.mapNodes({}, this.ATTACHMENT_FAKE))
                }
            }

            return parsedNode
        },
        async getHierarchicalCaseIndexTree() {
            this.items = []
            this.loading = true
            const [err, response] = await this.toCall(caseService.hierarchicalCaseIndexTree(this.laboratoryId, this.caseId, true))
            this.loading = false
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.caseNumber = response.casCasenumber
            response.samples.forEach(sampleTreeItem => {
                const sampleTreeItemNode = {
                    ...this.mapNodes(sampleTreeItem, ATTACHMENT_ENTITY_TYPE.SAMPLE)
                }
                this.items.push(sampleTreeItemNode)
            })
        },
        async selectAttachmentByFileName(filename) {
            this.selectByFileName = filename
        },
        async getSampleMacroImageAttachments(sampleId) {
            const [error, response] = await this.toCall(sampleAttachmentService.getAllMacroImageAttachments(
                this.laboratoryId,
                this.caseId,
                sampleId
            ))
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
            }
            return response
        },
        async getSubportionMacroImageAttachments(sampleId, portionId, subportionId) {
            const [error, response] = await this.toCall(subportionAttachmentService.getAllMacroImageAttachments(
                this.laboratoryId,
                this.caseId,
                sampleId,
                portionId,
                subportionId
            ))
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
            }
            return response
        }
    }
}
</script>

<style lang="scss" scoped>
    .spinner-wrapper {
        position: relative;
        width: 250px;
    }
    .fade-enter-active, .fade-leave-active {
        transition: opacity .5s;
    }
    .fade-enter, .fade-leave-to {
        opacity: 0;
    }
    .attachment p {
        font-size: font-size(small);
        word-break: break-word;
        white-space: normal;
        line-height: 14px;
        margin-bottom: 0;
        margin-top: 5px;
    }
    .viewer-module .treenode-attachment {
        width: 100% !important;
    }
</style>
